<template>
  <div>
    <b-row>
      <b-col>
        <div class="page-subtitle mt-5 mb-2">Roles</div>
        <p class="text-muted mb-3">
          Create roles and choose which parts of Tradie Pass each one can
          access. Assign a role to a Business Admin when you invite them or from
          their profile.
        </p>
      </b-col>
    </b-row>

    <div v-if="canCreateRoles" class="d-flex justify-content-end mb-3">
      <b-button variant="primary" class="create-role-btn" @click="openCreate">
        + Create Role
      </b-button>
    </div>

    <div v-if="isLoading" class="text-center py-5">
      <b-spinner variant="primary" />
    </div>

    <!-- Small screens: a plain full-width card list (avoids the stacked-table
         width quirks so each card fills the container). -->
    <div v-else-if="isStackedView" class="roles-cards">
      <div
        v-for="role in roles"
        :key="role.id"
        class="role-card"
        :class="{ 'role-card--clickable': canEditRoles }"
        @click="canEditRoles ? openEdit(role) : null"
      >
        <div class="role-card__title">
          <span class="font-weight-bold">{{ role.name }}</span>
          <b-badge v-if="role.is_system" variant="secondary" class="ml-2">
            System
          </b-badge>
        </div>
        <div class="role-card__row">
          <span class="role-card__label">Scope</span>
          <b-badge :variant="role.is_project_scoped ? 'info' : 'light'">
            {{ role.is_project_scoped ? 'Project-scoped' : 'Company-wide' }}
          </b-badge>
        </div>
        <div class="role-card__row">
          <span class="role-card__label">Permissions</span>
          <span>{{ permissionCount(role) }}</span>
        </div>
        <div class="role-card__row">
          <span class="role-card__label">Users</span>
          <span>{{ role.users_count != null ? role.users_count : '-' }}</span>
        </div>
        <div
          v-if="canEditRoles || canDeleteRoles"
          class="role-card__actions"
          @click.stop
        >
          <b-button
            v-if="canEditRoles"
            variant="outline-light"
            class="px-2"
            title="Edit role"
            @click="openEdit(role)"
          >
            <b-icon-pencil-square variant="primary" />
          </b-button>
          <b-button
            v-if="canDeleteRoles && !role.is_system"
            variant="outline-light"
            class="px-2"
            title="Delete role"
            @click="openDelete(role)"
          >
            <b-icon-trash-fill variant="danger" />
          </b-button>
        </div>
      </div>
      <div v-if="!roles.length" class="text-muted text-center py-4">
        No roles yet.
      </div>
    </div>

    <b-table
      v-else
      :fields="computedFields"
      :items="roles"
      :hover="canEditRoles || canDeleteRoles"
      class="roles-table"
      :class="{ 'roles-table--clickable': canEditRoles }"
      @row-clicked="canEditRoles ? openEdit($event) : null"
    >
      <template #cell(name)="{ item }">
        <span class="font-weight-bold">{{ item.name }}</span>
        <b-badge v-if="item.is_system" variant="secondary" class="ml-2">
          System
        </b-badge>
      </template>

      <template #cell(is_project_scoped)="{ item }">
        <b-badge :variant="item.is_project_scoped ? 'info' : 'light'">
          {{ item.is_project_scoped ? 'Project-scoped' : 'Company-wide' }}
        </b-badge>
      </template>

      <template #cell(permissions)="{ item }">
        {{ permissionCount(item) }}
      </template>

      <template #cell(users_count)="{ item }">
        {{ item.users_count != null ? item.users_count : '-' }}
      </template>

      <template #cell(actions)="{ item }">
        <div class="text-right" @click.stop>
          <b-button
            v-if="canEditRoles"
            variant="outline-light"
            class="px-2"
            title="Edit role"
            @click="openEdit(item)"
          >
            <b-icon-pencil-square variant="primary" />
          </b-button>
          <b-button
            v-if="canDeleteRoles && !item.is_system"
            variant="outline-light"
            class="px-2"
            title="Delete role"
            @click="openDelete(item)"
          >
            <b-icon-trash-fill variant="danger" />
          </b-button>
        </div>
      </template>
    </b-table>

    <role-editor-modal
      :show.sync="showEditor"
      :role-id="editingRoleId"
      @saved="loadRoles"
    />

    <b-modal v-model="showDeleteModal" centered @hidden="roleToDelete = null">
      <template #modal-title>
        <h3 class="font-weight-bold">Delete Role</h3>
      </template>
      <p>
        Delete the role <b>{{ roleToDelete ? roleToDelete.name : '' }}</b
        >? This can't be undone.
      </p>
      <p v-if="roleToDelete && roleToDelete.users_count" class="text-danger">
        This role is assigned to {{ roleToDelete.users_count }} user(s).
        Reassign them first or deletion will be rejected.
      </p>
      <template #modal-footer>
        <div class="d-flex" style="gap: 16px">
          <b-button variant="secondary" @click="showDeleteModal = false">
            Cancel
          </b-button>
          <b-button
            variant="danger"
            :disabled="deleting"
            @click="confirmDelete"
          >
            <b-spinner v-if="deleting" small class="mr-1" />
            Delete
          </b-button>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import RoleEditorModal from './RoleEditorModal.vue'

export default {
  name: 'RolesManager',

  components: { RoleEditorModal },

  data() {
    return {
      windowWidth: typeof window !== 'undefined' ? window.innerWidth : 1200,
      roles: [],
      isLoading: false,
      showEditor: false,
      editingRoleId: null,
      showDeleteModal: false,
      roleToDelete: null,
      deleting: false,
    }
  },

  computed: {
    ...mapGetters('auth', ['hasPermission']),

    /** Below md, render the roles as full-width cards instead of a table. */
    isStackedView() {
      return this.windowWidth < 768
    },

    canCreateRoles() {
      return this.hasPermission('settings.roles.create')
    },

    canEditRoles() {
      return this.hasPermission('settings.roles.edit')
    },

    canDeleteRoles() {
      return this.hasPermission('settings.roles.delete')
    },

    computedFields() {
      // Below md the table renders as stacked cards (see `stacked="md"`), which
      // show every field with a label — so no per-column hiding is needed. At
      // >=md it's the normal full table (the current desktop design).
      const base = [
        { key: 'name', label: 'Role' },
        { key: 'is_project_scoped', label: 'Scope' },
        { key: 'permissions', label: 'Permissions' },
        { key: 'users_count', label: 'Users' },
      ]
      if (this.canEditRoles || this.canDeleteRoles)
        base.push({ key: 'actions', label: '', tdClass: 'roles-actions-cell' })
      return base
    },
  },

  mounted() {
    window.addEventListener('resize', this.onResize)
    this.loadRoles()
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
  },

  methods: {
    onResize() {
      this.windowWidth = window.innerWidth
    },

    async loadRoles() {
      this.isLoading = true
      try {
        const { data } = await this.$api.get('company/roles')
        const list = (data && data.data) || data
        this.roles = Array.isArray(list) ? list : []
      } catch (e) {
        this.roles = []
      } finally {
        this.isLoading = false
      }
    },

    permissionCount(role) {
      if (Array.isArray(role.permission_keys)) {
        if (role.permission_keys.includes('*')) return 'Full access'
        return role.permission_keys.length
      }
      if (Array.isArray(role.permission_ids)) return role.permission_ids.length
      return '-'
    },

    openCreate() {
      this.editingRoleId = null
      this.showEditor = true
    },

    openEdit(role) {
      this.editingRoleId = role.id
      this.showEditor = true
    },

    openDelete(role) {
      this.roleToDelete = role
      this.showDeleteModal = true
    },

    async confirmDelete() {
      if (!this.roleToDelete) return
      this.deleting = true
      try {
        await this.$api.delete(`company/roles/${this.roleToDelete.id}`)
        this.showDeleteModal = false
        this.roleToDelete = null
        await this.loadRoles()
        this.$bvToast.toast('Role deleted.', {
          title: 'Success',
          variant: 'success',
          autoHideDelay: 3000,
        })
      } catch (err) {
        const message =
          (err.response && err.response.data && err.response.data.message) ||
          'Failed to delete role. It may still be assigned to users.'
        this.$bvToast.toast(message, {
          title: 'Oops.. Something went wrong!',
          variant: 'danger',
          autoHideDelay: 4000,
        })
      } finally {
        this.deleting = false
      }
    },
  },
}
</script>

<style scoped>
.roles-table--clickable >>> tbody tr {
  cursor: pointer;
}

/* Small screens: full-width role cards (plain divs — always fill the container).
   Each detail is a label-left / value-right row (spec-sheet style). */
.roles-cards {
  width: 100%;
}
.role-card {
  width: 100%;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 10px;
  padding: 0.75rem 1rem;
  margin-bottom: 0.75rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}
.role-card--clickable {
  cursor: pointer;
}
.role-card__title {
  font-size: 1.05rem;
  padding-bottom: 0.5rem;
  margin-bottom: 0.4rem;
  border-bottom: 1px solid #f1f3f5;
  word-break: break-word;
}
.role-card__row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  padding: 0.32rem 0;
}
.role-card__label {
  flex: 0 0 auto;
  font-weight: 700;
  text-transform: uppercase;
  font-size: 0.68rem;
  letter-spacing: 0.03em;
  color: #6c757d;
  white-space: nowrap;
}
.role-card__row > :last-child {
  text-align: right;
  min-width: 0;
  word-break: break-word;
}
.role-card__actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.6rem;
}

@media (max-width: 575.98px) {
  .create-role-btn {
    width: 100%;
  }
  .page-subtitle.mt-5 {
    margin-top: 1.25rem !important;
  }
}
@media (min-width: 576px) and (max-width: 991.98px) {
  .page-subtitle.mt-5 {
    margin-top: 2rem !important;
  }
}
</style>
